import { Controller, useFieldArray, useFormContext, useFormState } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { DashboardPageFormBody, DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../lib/messages';
import {
  createCustomTargetingGroup,
  createCustomTargetingKey,
  CustomTargetingOperator,
  CustomTargetingParameter,
} from '../../advancedTargetingModel';
import type { TargetingBuilderFormValue } from '../../builder/model';

export function CustomTargetingForm() {
  'use no memo';

  const { control } = useFormContext<TargetingBuilderFormValue>();
  const groups = useFieldArray({ control, name: 'customTargeting.groups' });
  return (
    <DashboardPageFormBody
      title={dashboardT('targetingBuilder.customTab')}
      description={dashboardT('targetingBuilder.customDescription')}
      width="lg"
    >
      <div className="grid gap-4">
        {groups.fields.map((group, index) => (
          <CustomTargetingGroup
            key={group.id}
            index={index}
            onRemove={() => {
              groups.remove(index);
            }}
          />
        ))}
        <Button
          type="button"
          variant="outline"
          className="w-fit"
          onClick={() => {
            groups.append(createCustomTargetingGroup());
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {dashboardT('targetingBuilder.addCustomGroup')}
        </Button>
      </div>
    </DashboardPageFormBody>
  );
}

function CustomTargetingGroup({ index, onRemove }: { index: number; onRemove: () => void }) {
  'use no memo';

  const { control, getFieldState, register } = useFormContext<TargetingBuilderFormValue>();
  const formState = useFormState({ control });
  const groupPath = `customTargeting.groups.${String(index)}` as `customTargeting.groups.${number}`;
  const keys = useFieldArray({ control, name: `${groupPath}.keys` });
  return (
    <section className="border-input grid gap-4 rounded-md border p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">
            {dashboardT('targetingBuilder.customGroup', { number: index + 1 })}
          </h3>
          <p className="text-muted-foreground text-sm">
            {dashboardT('targetingBuilder.customGroupLogic')}
          </p>
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={dashboardT('targetingBuilder.removeGroup')}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {keys.fields.map((key, keyIndex) => {
        const prefix =
          `${groupPath}.keys.${String(keyIndex)}` as `customTargeting.groups.${number}.keys.${number}`;
        const valueId = `custom-targeting-${key.id}-value`;
        return (
          <div key={key.id} className="grid gap-3 border-t pt-4 md:grid-cols-[1fr_1fr_2fr_auto]">
            <DashboardPageFormField label={dashboardT('targetingBuilder.operator')}>
              <Controller
                control={control}
                name={`${prefix}.operator`}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger aria-label={dashboardT('targetingBuilder.operator')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CustomTargetingOperator).map((value) => (
                        <SelectItem key={value} value={value}>
                          {dashboardT(`targetingBuilder.customOperators.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </DashboardPageFormField>
            <DashboardPageFormField label={dashboardT('targetingBuilder.parameter')}>
              <Controller
                control={control}
                name={`${prefix}.xpParameter`}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger aria-label={dashboardT('targetingBuilder.parameter')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(CustomTargetingParameter).map((value) => (
                        <SelectItem key={value} value={value}>
                          {value.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </DashboardPageFormField>
            <DashboardPageFormField
              label={dashboardT('targetingBuilder.value')}
              htmlFor={valueId}
              required
              error={getFieldState(`${prefix}.value`, formState).error?.message}
            >
              <Input
                id={valueId}
                required
                aria-invalid={Boolean(getFieldState(`${prefix}.value`, formState).error)}
                placeholder={dashboardT('targetingBuilder.valuePlaceholder')}
                {...register(`${prefix}.value`)}
              />
            </DashboardPageFormField>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="md:mt-[25px] md:self-start"
              aria-label={dashboardT('targetingBuilder.removeRule')}
              onClick={() => {
                keys.remove(keyIndex);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        className="w-fit"
        onClick={() => {
          keys.append(createCustomTargetingKey());
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {dashboardT('targetingBuilder.addRule')}
      </Button>
    </section>
  );
}
